// The Finance currency display: Trading | Local | Both. Every Finance screen opens in the organisation's trading
// currency; each person's last choice is remembered per screen on this device. Amounts stay stored in their own
// currency (the bill's, the payment's) and are converted for display: the rate stored on the payment, then the rate
// stored on the bill, then the organisation's rate for the date, then its default (fixed or latest) rate. Every
// converted figure has a small marker; hovering or tapping it shows the original amount, the rate and its date.
// Exports follow the choice (passed as ?display=).
import { esc, formatDate, get, money, toast, orgToday } from "../lib.js";
import { onAction, state } from "../state.js";
import { RateBook, type RateRow } from "../../../shared/rates.js";

export type Display = "trading" | "local" | "both";

/** The screen a choice is remembered for: the address without record numbers. */
export function screenKey(path = typeof location === "undefined" ? "" : location.pathname): string {
  return path.split("/").map((p) => (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(p) || /\d/.test(p) ? ":id" : p)).join("/") || "/";
}
const key = (screen = screenKey()) => `falcon.fin.display.${state.session.user?.id || ""}.${screen}`;
export function displayMode(local: string, trading: string, screen?: string): Display {
  if (local === trading) return "local";
  let v: string | null = null;
  try { v = localStorage.getItem(key(screen)); } catch { /* storage blocked */ }
  if (v === "trading" || v === "local" || v === "both") return v;
  return "trading";
}
export function setDisplayMode(v: Display, screen?: string) { try { localStorage.setItem(key(screen), v); } catch { /* storage blocked */ } }

/** The Trading | Local | Both switch (hidden when the two currencies are the same). */
export function displaySwitch(local: string, trading: string): string {
  if (local === trading) return "";
  const cur = displayMode(local, trading);
  const b = (v: Display, label: string) => `<button type="button" class="fin-disp-btn ${cur === v ? "active" : ""}" data-action="fin-display" data-v="${v}" aria-pressed="${cur === v}">${esc(label)}</button>`;
  return `<div class="fin-disp" role="group" aria-label="Show amounts in">${b("trading", `Trading (${trading})`)}${b("local", `Local (${local})`)}${b("both", "Both")}</div>`;
}
let redraw: (() => void) | null = null;
export function onDisplayChange(fn: () => void) { redraw = fn; }
onAction("fin-display", (el) => { setDisplayMode(el.dataset.v as Display); redraw?.(); });
// Tapping a converted figure (phones have no hover) shows where it came from.
onAction("fin-orig", (el, e) => { (e as Event | undefined)?.stopPropagation?.(); toast(el.getAttribute("title") || ""); });

/** The currency (or currencies) amounts are shown in on this screen, e.g. "USD" or "USD and SAR". */
export function shownCurrency(local: string, trading: string): string {
  const m = displayMode(local, trading);
  return m === "trading" ? trading : m === "local" ? local : `${trading} and ${local}`;
}
export const displayParam = (local: string, trading: string) => `display=${displayMode(local, trading)}`;

// ---- Rates for converting amounts on screen --------------------------------------------------------------------------------
let book: RateBook | null = null, bookAt = 0, bookOrg = "";
/** Loads the organisation's exchange rates (kept for a minute). Finance screens call it before drawing. */
export async function loadRates(): Promise<void> {
  const org = state.session.organisation?.id || "";
  if (book && bookOrg === org && Date.now() - bookAt < 60_000) return;
  try {
    const r = await get("/api/org/finance/rates");
    book = new RateBook((r.rows || []).map((x: any) => ({ from: x.from, to: x.to, rate: Number(x.rate), date: x.date || null, fixed: !!x.fixed })) as RateRow[], r.local);
    bookAt = Date.now(); bookOrg = org;
  } catch { book = null; }
}
const today = () => orgToday(); // today in the organisation's time zone
const r4 = (n: number) => Number(n.toFixed(4)).toString();
/** "1 USD = 3.75 SAR": a rate written the way people quote it (the stronger currency first). */
export function rateText(from: string, to: string, rate: number): string {
  if (!(rate > 0)) return "";
  return rate >= 1 ? `1 ${from} = ${r4(rate)} ${to}` : `1 ${to} = ${r4(1 / rate)} ${from}`;
}
export interface Orig { amount: number; currency: string; date?: string | null; rates?: Record<string, number | null | undefined> }
/** One shown figure: plain when it is in its own currency; with a marker and "Original: …" when it was converted. */
function shown(v: number | null | undefined, cur: string, dp: number, orig?: Orig | null): string {
  if (v === null || v === undefined) {
    // No exchange rate to this currency yet: the amount in its own currency, flagged, rather than nothing.
    const tip = `No exchange rate from ${orig?.currency || "this currency"} to ${cur} – add it in Settings → Finance → Exchange rates`;
    if (orig && orig.amount !== null && orig.amount !== undefined) return `<span class="fin-norate" data-action="fin-orig" tabindex="0" role="button" title="${esc(tip)}">${money(orig.amount, orig.currency, dp)}<sup class="fin-cv-mark" aria-hidden="true">!</sup></span>`;
    return `<span class="muted" title="${esc(tip)}">— ${esc(cur)}</span>`;
  }
  const text = money(v, cur, dp);
  if (!orig || orig.currency === cur || !orig.amount) return text;
  const given = orig.rates?.[cur];
  const rate = given && given > 0 ? given : v / orig.amount;
  const tip = `Original: ${money(orig.amount, orig.currency, dp)} at rate ${rateText(orig.currency, cur, rate)} on ${formatDate(orig.date || today())}`;
  return `<span class="fin-cv" data-action="fin-orig" tabindex="0" role="button" title="${esc(tip)}" aria-label="${esc(`${text}. ${tip}`)}">${text}<sup class="fin-cv-mark" aria-hidden="true">≈</sup></span>`;
}

/** A converted amount pair ({ local, trading }) as HTML for the current display. `orig` (the amount in its own
 *  currency) adds the marker and "Original: …" to every figure in another currency. */
export function conv(x: { local?: number | null; trading?: number | null } | null | undefined, local: string, trading: string, dp = 2, orig?: Orig | null): string {
  if (!x) return "—";
  const mode = displayMode(local, trading);
  const o = (_target: string) => orig || null;
  if (local === trading || mode === "local") return shown(x.local, local, dp, o(local));
  if (mode === "trading") return shown(x.trading, trading, dp, o(trading));
  return `${shown(x.trading, trading, dp, o(trading))}<br><small class="muted">${shown(x.local, local, dp, o(local))}</small>`;
}
/** A local-currency amount converted with the local → trading rate (figures worked out in the local currency). */
export function convLocal(n: number | null | undefined, local: string, trading: string, l2t: number | null, dp = 2, date?: string | null): string {
  if (n === null || n === undefined) return "—";
  return conv({ local: n, trading: l2t === null ? null : round(n * l2t, dp) }, local, trading, dp, { amount: n, currency: local, date, rates: { [trading]: l2t } });
}

export interface FxOpt {
  /** The date of the amount (bill / payment date): the rate for that date is used when none is stored. */
  date?: string | null;
  /** Rates stored on the record (the payment's, else the bill's): amount × rate = local / trading. */
  toLocal?: number | null; toTrading?: number | null;
  /** Already-converted figures from the server (at the stored rates). */
  local?: number | null; trading?: number | null;
  dp?: number;
}
/** The amount converted into one currency: stored figure → stored rate → the organisation's rate for the date →
 *  its default rate. Null when there is no rate. */
export function fxValue(n: number | null | undefined, cur: string, target: string, o: FxOpt = {}): number | null {
  if (n === null || n === undefined) return null;
  const dp = o.dp ?? 2;
  if (cur === target) return n;
  const isLocal = target === fxCtxOf().L, isTrading = target === fxCtxOf().T;
  if (isTrading && o.trading !== undefined && o.trading !== null) return o.trading;
  if (isLocal && o.local !== undefined && o.local !== null) return o.local;
  const r = fxRate(cur, target, o);
  return r === null ? null : round(n * r, dp);
}
/** The rate used for one amount: the stored rate, else the organisation's rate for the date (or its default). */
export function fxRate(cur: string, target: string, o: FxOpt = {}): number | null {
  if (cur === target) return 1;
  const stored = target === fxCtxOf().T ? o.toTrading : target === fxCtxOf().L ? o.toLocal : null;
  if (stored) return stored;
  return book?.rate(cur, target, o.date || null) ?? null;
}
let fxSet = { L: "", T: "" };
/** The organisation's local and trading currency for fx() on this screen (from the session unless a screen says). */
export function fxUse(local: string, trading: string) { fxSet = { L: local, T: trading }; }
const fxCtxOf = () => {
  if (fxSet.L && fxSet.T) return fxSet;
  const o = state.session.organisation;
  return { L: o?.currency || "", T: o?.tradingCurrency || o?.currency || "" };
};
/** An amount in its own currency, shown in the screen's display currency (converted, with the marker). */
export function fx(n: number | null | undefined, cur: string, o: FxOpt = {}): string {
  if (n === null || n === undefined) return "—";
  const { L, T } = fxCtxOf(), dp = o.dp ?? 2;
  if (!L || !T) return money(n, cur, dp);
  const pre = (x: number | null | undefined) => (x !== null && x !== undefined && n ? x / n : null);
  const orig: Orig = { amount: n, currency: cur, date: o.date, rates: { [L]: pre(o.local) ?? fxRate(cur, L, o), [T]: pre(o.trading) ?? fxRate(cur, T, o) } };
  return conv({ local: fxValue(n, cur, L, o), trading: fxValue(n, cur, T, o) }, L, T, dp, orig);
}
/** The amount in the main display currency (trading for Trading and Both, local for Local), for totals. */
export function fxMain(n: number | null | undefined, cur: string, o: FxOpt = {}): number | null {
  const { L, T } = fxCtxOf();
  return fxValue(n, cur, displayMode(L, T) === "local" ? L : T, o);
}
/** The currency the main figures are in (trading for Trading and Both, local for Local). */
export const mainCurrency = () => (displayMode(fxCtxOf().L, fxCtxOf().T) === "local" ? fxCtxOf().L : fxCtxOf().T);

export const round = (n: number, dp = 2) => { const f = 10 ** dp; return Math.round((n + Math.sign(n) * 1e-9) * f) / f; };
/** Rounds each line and puts any rounding difference on the last line, so the lines add up to the total. */
export function balance(values: Array<number | null>, total: number | null, dp = 2): Array<number | null> {
  const out = values.map((v) => (v === null ? null : round(v, dp)));
  if (total === null || out.some((v) => v === null) || !out.length) return out;
  const diff = round(round(total, dp) - out.reduce((t: number, v) => t + (v as number), 0), dp);
  if (diff) out[out.length - 1] = round((out[out.length - 1] as number) + diff, dp);
  return out;
}
/** Sum of shown (rounded) figures. */
export const sumShown = (values: Array<number | null>, dp = 2) => (values.some((v) => v === null) ? null : round(values.reduce((t: number, v) => t + (v as number), 0), dp));
/** One figure in a currency, with the marker and "Original: …" when `orig` is in another currency. */
export const figure = shown;
